import { useAuth } from "../contexts/auth-context"
import { useNavigate } from "react-router-dom"
import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { Skeleton } from "../components/ui/skeleton"
import { Brain, Building, DollarSign, TrendingUp, AlertTriangle, Plus, FileText, BarChart3, Activity, Calculator, Receipt, BookOpen, Clock, CheckCircle, Edit, CreditCard, Sparkles, ArrowRight, Package } from "lucide-react"
import { PageLayout } from "../components/page-layout"
import { useDashboardData } from "../hooks/useDashboardData"
import { getCompanyId } from "../lib/config"
import { MoonLoader } from "../components/ui/moon-loader"
import { useQuery } from "@tanstack/react-query"
import apiService from "../lib/api"
import { bankingApi, type BankAccount } from "../lib/api/banking"
import { TranslatedText } from "../components/translated-text"
import { useTranslation } from "../hooks/useTranslation"

export default function Dashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [companyId, setCompanyId] = useState<string>(() => {
    try {
      return getCompanyId()
    } catch (error) {
      return ''
    }
  })
  
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  // Redirect restricted roles to their specific modules
  useEffect(() => {
    if (!isMounted) return;

    // Check if user is waiter or receptionist (manager is excluded)
    if (user?.role && ['waiter', 'receptionist'].includes(user.role)) {
      const accessScope = (user as any).accessScope || [];
      const hasPos = accessScope.includes('POS');
      const hasHotel = accessScope.includes('HOTEL');

      // Short delay to ensure navigation is stable
      const redirectTimer = setTimeout(() => {
        // If they have POS access and (they are a waiter OR they don't have Hotel access)
        if (hasPos && (user.role === 'waiter' || !hasHotel)) {
          console.log('[Dashboard] Navigating to /dashboard/pos');
          navigate('/dashboard/pos', { replace: true });
          return;
        }

        // If they have Hotel access and (they are a receptionist OR they don't have POS access)
        if (hasHotel && (user.role === 'receptionist' || !hasPos)) {
          console.log('[Dashboard] Navigating to /dashboard/hotel');
          navigate('/dashboard/hotel', { replace: true });
          return;
        }
        
        // Fallback for edge cases
        if (hasPos) {
          console.log('[Dashboard] Navigating to /dashboard/pos (fallback)');
          navigate('/dashboard/pos', { replace: true });
        } else if (hasHotel) {
          console.log('[Dashboard] Navigating to /dashboard/hotel (fallback)');
          navigate('/dashboard/hotel', { replace: true });
        }
      }, 100);

      return () => clearTimeout(redirectTimer);
    }
  }, [user, navigate, isMounted]);

  const { data: dashboardData, loading, error } = useDashboardData(companyId)

  // Fetch journal entries statistics
  interface JournalStats {
    totalEntries?: number;
    postedToday?: number;
    draftEntries?: number;
    postedEntries?: number;
    pendingApproval?: number;
  }

  const { data: journalStats, isLoading: journalStatsLoading, error: journalStatsError } = useQuery<JournalStats>({
    queryKey: ['journal-stats', companyId],
    queryFn: async () => {
      try {
        const result: any = await apiService.getJournalSummary({ companyId: companyId || '' });
        // Handle both response formats: wrapped in summary or direct
        if (result?.summary) {
          return result.summary as JournalStats;
        }
        return result as JournalStats;
      } catch (error) {
        console.error('❌ Error in journal stats queryFn:', error);
        throw error;
      }
    },
    enabled: !!companyId
  })


  // Fetch pending approvals separately
  interface PendingApprovalsData {
    pendingApprovals?: any[];
  }

  const { data: pendingApprovalsData, isLoading: approvalsLoading } = useQuery<PendingApprovalsData>({
    queryKey: ['journal-pending-approvals', companyId],
    queryFn: async () => {
      const result = await apiService.getPendingApprovals();
      return result;
    },
    enabled: !!companyId
  })

  // Fetch bank accounts
  const { data: bankAccounts, isLoading: bankAccountsLoading } = useQuery<BankAccount[]>({
    queryKey: ['bank-accounts', companyId],
    queryFn: async () => {
      const result = await bankingApi.getBankAccounts(companyId || '');
      return result || [];
    },
    enabled: !!companyId
  })

  // Calculate bank account totals
  const totalBankBalance = bankAccounts?.reduce((sum, account) => {
    const balance = Number(account.balance) || 0;
    return sum + balance;
  }, 0) || 0;
  
  const activeBankAccounts = bankAccounts?.filter(acc => acc.status === 'active') || [];
  const totalAccounts = activeBankAccounts.length;

  // Listen for company changes from header
  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const newCompanyId = getCompanyId();
        if (newCompanyId && newCompanyId !== companyId) {
          setCompanyId(newCompanyId);
        }
      } catch (error) {
        // Company ID not available yet
      }
    };

    // Listen for localStorage changes
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events (in case localStorage doesn't trigger)
    const handleCompanyChange = (e: CustomEvent) => {
      const newCompanyId = e.detail.companyId;
      if (newCompanyId && newCompanyId !== companyId) {
        setCompanyId(newCompanyId);
      }
    };
    
    window.addEventListener('companyChanged', handleCompanyChange as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('companyChanged', handleCompanyChange as EventListener);
    };
  }, [companyId]);

  // Wait for company ID to be properly set by header before fetching data
  useEffect(() => {
    const checkForCompanyId = () => {
      try {
        const newCompanyId = getCompanyId();
        if (newCompanyId && newCompanyId !== companyId) {
          setCompanyId(newCompanyId);
        }
      } catch (error) {
        // Company ID still not available, will check again
      }
    };

    // Check immediately
    checkForCompanyId();
    
    // Then check every 500ms until we have a valid company ID
    const interval = setInterval(checkForCompanyId, 500);
    
    return () => clearInterval(interval);
  }, [companyId]);

  // Calculate invoice totals
  const unpaidInvoices = (dashboardData?.metrics?.pendingInvoices || 0) + (dashboardData?.metrics?.overdueInvoices || 0)
  const totalRevenue = dashboardData?.metrics?.totalRevenue || 0
  const totalExpenses = dashboardData?.metrics?.totalExpenses || 0
  const netProfit = dashboardData?.metrics?.netProfit || 0
  const profitMargin = dashboardData?.metrics?.profitMargin || 0

  // Get last 4 months of sales data for chart
  const salesData = dashboardData?.monthlyTrend?.slice(-4) || []
  const maxSales = Math.max(...salesData.map((item: any) => item.revenue || item.totalRevenue || 0), totalRevenue || 1)

  // Expense categories for donut chart - use real data from API
  // Note: Backend already includes uncategorized expenses in the expenseCategories array
  const expenseCategories = (dashboardData?.expenseCategories || []).map((cat: any) => ({
    name: cat.name,
    amount: Number(cat.amount || 0),
    color: cat.color || 'bg-gray-300'
  })).filter((cat: any) => cat.amount > 0)
  
  // Calculate totals for display
  const categorizedTotal = expenseCategories.reduce((sum, cat) => sum + cat.amount, 0)
  // Only count categorized expenses (excluding uncategorized) for the percentage
  const categorizedOnly = expenseCategories.filter(cat => {
    const nameLower = cat.name.toLowerCase()
    return !nameLower.includes('uncategorized') && !nameLower.includes('to review')
  })
  const categorizedOnlyTotal = categorizedOnly.reduce((sum, cat) => sum + cat.amount, 0)
  
  // Use the actual expense table total from categories (which includes uncategorized)
  // If no categories returned but we have expenses, use totalExpenses as fallback
  const expenseTableTotal = categorizedTotal > 0 ? categorizedTotal : totalExpenses
  const categorizedPercentage = expenseTableTotal > 0 ? Math.round((categorizedOnlyTotal / expenseTableTotal) * 100) : 0
  
  // Use expenseCategories directly - backend already includes uncategorized if it exists
  // If we have expenses but no categories returned, show all as uncategorized
  const allExpenseCategories = expenseCategories.length > 0 
    ? expenseCategories 
    : (totalExpenses > 0 ? [{ name: 'Uncategorized', amount: totalExpenses, color: 'bg-gray-300' }] : [])

  // Show loading if no company ID is available yet
  if (!companyId) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center h-64">
          <MoonLoader size="md" color="teal" />
          <span className="ml-2">Loading company data...</span>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="space-y-6 pr-12">
          
          {/* Page Header - Keep System Health in Header */}
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-4 md:p-6 border border-cyan-100 shadow-sm">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent break-words">
                <TranslatedText text="Dashboard" />
              </h1>
              <p className="text-muted-foreground mt-1 text-sm sm:text-base md:text-lg">
                <TranslatedText text="Financial Overview & Analytics" />
              </p>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-green-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <TranslatedText text="System Healthy" />
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">
                  <TranslatedText text="Last updated" />: {new Date().toLocaleTimeString()}
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row w-full md:w-auto gap-2 sm:gap-3">
              <Button
                variant="outline"
                className="w-full sm:w-auto justify-center hover:bg-cyan-50 hover:border-cyan-200"
                onClick={() => navigate('/dashboard/accounting')}
              >
                <Activity className="mr-2 h-4 w-4" />
                <TranslatedText text="Export Report" />
              </Button>
              <Button
                className="w-full sm:w-auto justify-center bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                onClick={() => navigate('/dashboard/accounting')}
              >
                <BarChart3 className="mr-2 h-4 w-4" />
                <TranslatedText text="View Analytics" />
              </Button>
            </div>
          </div>

          {/* Business Overview Cards - First Row - 3 Columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* INVOICES Card */}
            <Card className="border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-gray-700">
                  <TranslatedText text="INVOICES" />
                </CardTitle>
                <CardDescription className="text-xs text-gray-500">
                  {loading ? (
                    <Skeleton className="h-3 w-32 bg-gray-200" />
                  ) : (
                    <>
                      {unpaidInvoices.toLocaleString()} Frw <TranslatedText text="Unpaid" /> - <TranslatedText text="Last 365 days" />
                    </>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {loading ? (
                  <>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Skeleton className="h-3 w-16 bg-gray-200" />
                        <Skeleton className="h-4 w-20 bg-gray-200" />
                      </div>
                      <Skeleton className="h-2 w-full rounded-full bg-gray-200" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Skeleton className="h-3 w-20 bg-gray-200" />
                        <Skeleton className="h-4 w-20 bg-gray-200" />
                      </div>
                      <Skeleton className="h-2 w-full rounded-full bg-gray-200" />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-600">
                          <TranslatedText text="Due now" />
                        </span>
                        <span className="text-sm font-semibold text-gray-900">
                          {(dashboardData?.metrics?.overdueInvoices || 0).toLocaleString()} Frw
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-orange-500 h-2 rounded-full" 
                          style={{ width: `${Math.min(((dashboardData?.metrics?.overdueInvoices || 0) / Math.max(unpaidInvoices, 1)) * 100, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-600">
                          <TranslatedText text="Not due yet" />
                        </span>
                        <span className="text-sm font-semibold text-gray-900">
                          {(dashboardData?.metrics?.pendingInvoices || 0).toLocaleString()} Frw
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${(dashboardData?.metrics?.pendingInvoices || 0) > 0 ? 'bg-primary' : 'bg-gray-400'}`}
                          style={{ width: `${Math.min(((dashboardData?.metrics?.pendingInvoices || 0) / Math.max(unpaidInvoices, 1)) * 100, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* EXPENSES Card */}
            <Card className="border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-gray-700">
                  <TranslatedText text="EXPENSES" />
                </CardTitle>
                <CardDescription className="text-xs text-gray-500">
                  <TranslatedText text="Spending for" /> {new Date().toLocaleString('default', { month: 'long' })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <Skeleton className="h-8 w-32 bg-gray-200" />
                      <Skeleton className="h-4 w-24 bg-gray-200" />
                    </div>
                    <div className="flex gap-4">
                      <Skeleton className="w-24 h-24 rounded-full bg-gray-200" />
                      <div className="flex-1 space-y-2">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="flex items-center gap-2">
                            <Skeleton className="w-2 h-2 rounded-full bg-gray-200" />
                            <Skeleton className="h-3 w-24 bg-gray-200" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-2xl font-bold text-gray-900">
                        {totalExpenses.toLocaleString()} Frw
                      </div>
                      <div className="flex items-center gap-1 text-xs text-blue-600">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span>{categorizedPercentage}% <TranslatedText text="categorized" /></span>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      {/* Donut Chart */}
                      <div className="relative w-24 h-24 flex-shrink-0">
                        {allExpenseCategories.length > 0 && expenseTableTotal > 0 ? (
                          <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                            {allExpenseCategories.map((cat, index) => {
                              const prevSum = allExpenseCategories.slice(0, index).reduce((sum, c) => sum + c.amount, 0)
                              // Use expenseTableTotal (not totalExpenses) for chart percentages
                              // This matches the backend calculation which only includes expense table expenses
                              const percentage = expenseTableTotal > 0 ? (cat.amount / expenseTableTotal) * 100 : 0
                              const startAngle = expenseTableTotal > 0 ? (prevSum / expenseTableTotal) * 360 : 0
                              const endAngle = expenseTableTotal > 0 ? ((prevSum + cat.amount) / expenseTableTotal) * 360 : 0
                              
                              // Convert angles to radians
                              const startRad = (startAngle - 90) * (Math.PI / 180)
                              const endRad = (endAngle - 90) * (Math.PI / 180)
                              
                              // Calculate arc path
                              // For full circle (360 degrees), use large arc flag
                              const isFullCircle = Math.abs(endAngle - startAngle) >= 360
                              const largeArcFlag = isFullCircle ? 1 : (percentage > 50 ? 1 : 0)
                              
                              const x1 = 50 + 40 * Math.cos(startRad)
                              const y1 = 50 + 40 * Math.sin(startRad)
                              const x2 = 50 + 40 * Math.cos(endRad)
                              const y2 = 50 + 40 * Math.sin(endRad)
                              
                              // For full circle, create a complete circle path
                              let pathData: string
                              if (isFullCircle || Math.abs(endAngle - startAngle) >= 359.9) {
                                // Full circle - draw complete circle using two semicircles
                                // Start at top (0 degrees after rotation)
                                const topX = 50
                                const topY = 10
                                pathData = `M 50 50 L ${topX} ${topY} A 40 40 0 1 1 50 90 A 40 40 0 1 1 ${topX} ${topY} Z`
                              } else {
                                // Partial arc
                                pathData = `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2} Z`
                              }
                              
                              // Map color to hex value - default to teal brand colors
                              const getColorHex = (color: string) => {
                                // Teal shades (brand colors)
                                if (color.includes('teal-600') || color === '#0d9488') return '#0d9488';
                                if (color.includes('teal-500') || color === '#14b8a6') return '#14b8a6';
                                if (color.includes('teal-400') || color === '#2dd4bf') return '#2dd4bf';
                                if (color.includes('teal-300') || color === '#5eead4') return '#5eead4';
                                if (color.includes('teal-200') || color === '#99f6e4') return '#99f6e4';
                                // Gray for uncategorized
                                if (color.includes('gray-300') || color === '#d1d5db') return '#d1d5db';
                                if (color.includes('gray-200') || color === '#e5e7eb') return '#e5e7eb';
                                // If color is already a hex code, use it
                                if (color.startsWith('#')) return color;
                                // Default to teal-600 (brand color)
                                return '#0d9488';
                              }
                              
                              return (
                                <path
                                  key={cat.name || `category-${index}`}
                                  d={pathData}
                                  fill={getColorHex(cat.color)}
                                  className="transition-all"
                                />
                              )
                            })}
                            {/* Inner circle to create donut effect */}
                            <circle cx="50" cy="50" r="25" fill="white" />
                          </svg>
                        ) : (
                          <div className="w-24 h-24 rounded-full border-2 border-gray-200 flex items-center justify-center">
                            <span className="text-xs text-gray-400">
                              <TranslatedText text="No data" />
                            </span>
                          </div>
                        )}
                      </div>
                  {/* Categories List */}
                  <div className="flex-1 space-y-2">
                    {allExpenseCategories.map((cat) => (
                      <div key={cat.name} className="flex items-center gap-2 text-xs">
                        <div 
                          className="w-2 h-2 rounded-full"
                          style={{ 
                            backgroundColor: cat.color.startsWith('#') ? cat.color : 
                              cat.color.includes('teal-600') ? '#0d9488' : 
                              cat.color.includes('teal-500') ? '#14b8a6' :
                              cat.color.includes('teal-400') ? '#2dd4bf' :
                              cat.color.includes('teal-300') ? '#5eead4' :
                              cat.color.includes('teal-200') ? '#99f6e4' :
                              cat.color.includes('gray-300') ? '#d1d5db' :
                              cat.color.includes('gray-200') ? '#e5e7eb' :
                              '#0d9488' // Default to teal-600 (brand color)
                          }}
                        ></div>
                        <span className="text-gray-700">{cat.name}</span>
                        <span className="text-gray-500 ml-auto">
                          {expenseTableTotal > 0 ? Math.round((cat.amount / expenseTableTotal) * 100) : 0}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* BANK ACCOUNTS Card */}
            <Card className="border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-gray-700">
                  <TranslatedText text="BANK ACCOUNTS" />
                </CardTitle>
                <CardDescription className="text-xs text-gray-500">
                  {bankAccountsLoading ? (
                    <TranslatedText text="Loading..." />
                  ) : totalAccounts > 0 ? (
                    <>
                      {totalAccounts} {totalAccounts === 1 ? <TranslatedText text="account" /> : <TranslatedText text="accounts" />}
                    </>
                  ) : (
                    <TranslatedText text="No accounts connected" />
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {bankAccountsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <MoonLoader size="sm" color="teal" />
                  </div>
                ) : totalAccounts > 0 ? (
                  <div className="space-y-3">
                    <div className="text-2xl font-bold text-gray-900">
                      {totalBankBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Frw
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">
                          <TranslatedText text="Total Balance" />
                        </span>
                        <span className="font-medium text-gray-900">{totalBankBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Frw</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">
                          <TranslatedText text="Active Accounts" />
                        </span>
                        <span className="font-medium text-gray-900">{totalAccounts}</span>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-gray-200 space-y-2">
                      <Button 
                        variant="link" 
                        className="text-xs p-0 h-auto text-primary w-full justify-start" 
                        onClick={() => navigate('/dashboard/banking')}
                      >
                        <CreditCard className="w-3 h-3 mr-1" />
                        <TranslatedText text="Go to banking" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-sm text-gray-500 text-center py-4">
                      <TranslatedText text="No bank accounts connected" />
                    </div>
                    <div className="space-y-2">
                      <Button 
                        variant="link" 
                        className="text-xs p-0 h-auto text-primary w-full justify-start" 
                        onClick={() => navigate('/dashboard/banking')}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        <TranslatedText text="Connect accounts" />
                      </Button>
                      <Button 
                        variant="link" 
                        className="text-xs p-0 h-auto text-primary w-full justify-start" 
                        onClick={() => navigate('/dashboard/banking')}
                      >
                        <CreditCard className="w-3 h-3 mr-1" />
                        <TranslatedText text="Go to registers" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Second Row - 3 Column Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* PROFIT & LOSS Card */}
            <Card className="border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-gray-700">
                  <TranslatedText text="PROFIT & LOSS" />
                </CardTitle>
                <CardDescription className="text-xs text-gray-500">
                  <TranslatedText text="Net profit for" /> {new Date().toLocaleString('default', { month: 'long' })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <>
                    <div className="flex items-center gap-2 mb-3">
                      <Skeleton className="h-8 w-32 bg-gray-200" />
                      <Skeleton className="h-4 w-40 bg-gray-200" />
                    </div>
                    <div className="space-y-2">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <Skeleton className="h-3 w-16 bg-gray-200" />
                          <Skeleton className="h-4 w-24 bg-gray-200" />
                        </div>
                        <Skeleton className="h-2 w-full rounded-full bg-gray-200" />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <Skeleton className="h-3 w-20 bg-gray-200" />
                          <Skeleton className="h-4 w-24 bg-gray-200" />
                        </div>
                        <Skeleton className="h-2 w-full rounded-full bg-gray-200" />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="text-2xl font-bold text-gray-900">{netProfit.toLocaleString()} Frw</span>
                        <div className="flex items-center gap-1 mt-1">
                          <span className="text-sm font-semibold text-gray-700">
                            <TranslatedText text="Profit Margin" />:
                          </span>
                          <span className={`text-sm font-bold ${profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {profitMargin.toFixed(2)}%
                          </span>
                        </div>
                      </div>
                      {netProfit > 0 && (
                        <div className="flex items-center gap-1 text-xs text-green-600">
                          <TrendingUp className="h-3 w-3" />
                          <span><TranslatedText text="Up 100%" /></span>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-600">
                            <TranslatedText text="Income" />
                          </span>
                          <span className="text-sm font-semibold text-gray-900">{totalRevenue.toLocaleString()} Frw</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full" 
                            style={{ width: `${Math.min((totalRevenue / Math.max(totalRevenue + totalExpenses, 1)) * 100, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-600">
                            <TranslatedText text="Expenses" />
                          </span>
                          <span className="text-sm font-semibold text-gray-900">{totalExpenses.toLocaleString()} Frw</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-teal-500 h-2 rounded-full" 
                            style={{ width: `${Math.min((totalExpenses / Math.max(totalRevenue + totalExpenses, 1)) * 100, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* SALES Card */}
            <Card className="border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-gray-700">
                  <TranslatedText text="SALES" />
                </CardTitle>
                <CardDescription className="text-xs text-gray-500">
                  <TranslatedText text="Last 4 months comparison" />
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <>
                    <Skeleton className="h-8 w-32 mb-3 bg-gray-200" />
                    <div className="h-24 flex items-end justify-between gap-1">
                      {[1, 2, 3, 4].map((i) => (
                        <Skeleton 
                          key={i} 
                          className="flex-1 rounded-t bg-gray-200"
                          style={{ height: `${40 + i * 10}%` }}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-2xl font-bold text-gray-900 mb-3">
                      {totalRevenue.toLocaleString()} Frw
                    </div>
                    <div className="space-y-2">
                      <div className="h-24 flex items-end justify-between gap-1">
                        {salesData.length > 0 ? (
                          salesData.map((item: any, index: number) => {
                            const revenue = item.revenue || item.totalRevenue || 0
                            const height = (revenue / maxSales) * 100
                            return (
                              <div 
                                key={index} 
                                className="flex-1 bg-primary rounded-t hover:bg-primary/80 transition-colors relative group"
                                style={{ height: `${Math.max(height, 10)}%` }}
                                title={`${item.month || 'Month'}: ${revenue.toLocaleString()} Frw`}
                              >
                                {/* Tooltip on hover */}
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                  {revenue.toLocaleString()} Frw
                                </div>
                              </div>
                            )
                          })
                        ) : (
                          [1, 2, 3, 4].map((i) => (
                            <div 
                              key={i} 
                              className="flex-1 bg-primary rounded-t"
                              style={{ height: `${40 + i * 10}%` }}
                            ></div>
                          ))
                        )}
                      </div>
                      {/* Month labels */}
                      <div className="flex items-center justify-between gap-1 text-xs text-gray-500">
                        {salesData.length > 0 ? (
                          salesData.map((item: any, index: number) => {
                            // Translate month names
                            const monthName = item.month || `M${index + 1}`;
                            // Check if it's a valid month name to translate
                            const validMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                            
                            return (
                              <div key={index} className="flex-1 text-center truncate" title={validMonths.includes(monthName) ? <TranslatedText text={monthName} /> : monthName}>
                                {validMonths.includes(monthName) ? <TranslatedText text={monthName} /> : monthName}
                              </div>
                            );
                          })
                        ) : (
                          ['M1', 'M2', 'M3', 'M4'].map((label, i) => (
                            <div key={i} className="flex-1 text-center truncate">
                              {label}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* DISCOVER MORE IN MY SYSTEMS Card */}
            <Card className="border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-gray-700 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-gray-600" />
                  <TranslatedText text="DISCOVER MORE IN MY SYSTEMS" />
                </CardTitle>
                <CardDescription className="text-xs text-gray-500">
                  <TranslatedText text="Explore additional features and tools" />
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="text-sm text-gray-700">
                    <TranslatedText text="Unlock more capabilities to streamline your business operations." />
                  </div>
                  <div className="space-y-2">
                    <Button 
                      variant="outline" 
                      className="w-full justify-between text-xs h-9 border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-gray-700 hover:text-gray-900"
                      onClick={() => navigate('/dashboard/accounting')}
                    >
                      <span className="flex items-center gap-2">
                        <BarChart3 className="w-3 h-3" />
                        <span><TranslatedText text="View Reports" /></span>
                      </span>
                      <ArrowRight className="w-3 h-3" />
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-between text-xs h-9 border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-gray-700 hover:text-gray-900"
                      onClick={() => navigate('/dashboard/accounting')}
                    >
                      <span className="flex items-center gap-2">
                        <Calculator className="w-3 h-3" />
                        <span><TranslatedText text="Accounting Hub" /></span>
                      </span>
                      <ArrowRight className="w-3 h-3" />
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full justify-between text-xs h-9 border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-gray-700 hover:text-gray-900"
                      onClick={() => navigate('/dashboard/inventory')}
                    >
                      <span className="flex items-center gap-2">
                        <Package className="w-3 h-3" />
                        <span><TranslatedText text="Inventory" /></span>
                      </span>
                      <ArrowRight className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions & Recent Activity - Side by Side */}
          <div className="grid gap-6 lg:grid-cols-2 lg:gap-8">
            {/* Quick Actions */}
            <Card className="border-gray-100 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5 text-blue-600" />
                  <TranslatedText text="Quick Actions" />
                </CardTitle>
                <CardDescription>
                  <TranslatedText text="Common tasks and shortcuts to get you started" />
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Button 
                    variant="outline" 
                    className="h-28 flex-col gap-2 hover:bg-gradient-to-br hover:from-green-50 hover:to-emerald-50 hover:border-green-300 transition-all duration-300 p-3 group"
                    onClick={() => navigate('/dashboard/sales')}
                  >
                    <div className="p-2 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
                      <DollarSign className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="text-center">
                      <span className="font-semibold text-xs text-gray-900 block">
                        <TranslatedText text="Create Invoice" />
                      </span>
                    </div>
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="h-28 flex-col gap-2 hover:bg-gradient-to-br hover:from-teal-50 hover:to-cyan-50 hover:border-teal-300 transition-all duration-300 p-3 group"
                    onClick={() => navigate('/dashboard/journal/new')}
                  >
                    <div className="p-2 bg-teal-100 rounded-lg group-hover:bg-teal-200 transition-colors">
                      <FileText className="h-5 w-5 text-teal-600" />
                    </div>
                    <div className="text-center">
                      <span className="font-semibold text-xs text-gray-900 block">
                        <TranslatedText text="New Journal Entry" />
                      </span>
                    </div>
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="h-28 flex-col gap-2 hover:bg-gradient-to-br hover:from-teal-50 hover:to-cyan-50 hover:border-teal-300 transition-all duration-300 p-3 group"
                    onClick={() => navigate('/dashboard/reports')}
                  >
                    <div className="p-2 bg-teal-100 rounded-lg group-hover:bg-teal-200 transition-colors">
                      <TrendingUp className="h-5 w-5 text-teal-600" />
                    </div>
                    <div className="text-center">
                      <span className="font-semibold text-xs text-gray-900 block">
                        <TranslatedText text="View Reports" />
                      </span>
                    </div>
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="h-28 flex-col gap-2 hover:bg-gradient-to-br hover:from-teal-50 hover:to-cyan-50 hover:border-teal-300 transition-all duration-300 p-3 group"
                    onClick={() => navigate('/dashboard/accounting')}
                  >
                    <div className="p-2 bg-teal-100 rounded-lg group-hover:bg-teal-200 transition-colors">
                      <Calculator className="h-5 w-5 text-teal-600" />
                    </div>
                    <div className="text-center">
                      <span className="font-semibold text-xs text-gray-900 block">
                        <TranslatedText text="Chart of Accounts" />
                      </span>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-gray-600" />
                  <TranslatedText text="Recent Activity" />
                </CardTitle>
                <CardDescription>
                  <TranslatedText text="Latest updates and changes across your account" />
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <MoonLoader size="sm" className="mr-2" color="teal" />
                      <span className="text-sm text-gray-500">
                        <TranslatedText text="Loading recent activity..." />
                      </span>
                    </div>
                  ) : (dashboardData?.recentActivity?.length || 0) > 0 ? (
                    <>
                      {/* Show network warning if offline but we have cached data */}
                      {error && (error.includes('No internet connection') || error.includes('network')) && (
                        <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-xs">
                          <AlertTriangle className="h-4 w-4" />
                          <span>
                            <TranslatedText text="No internet connection - showing cached data" />
                          </span>
                        </div>
                      )}
                      {dashboardData?.recentActivity?.slice(0, 3).map((activity) => {
                      // Translate activity type
                      const activityTypeText = activity.type === 'journal_entry' 
                        ? 'Journal Entry' 
                        : activity.type === 'invoice' 
                        ? 'Invoice' 
                        : activity.type === 'bill'
                        ? 'Bill'
                        : activity.type === 'payment'
                        ? 'Payment'
                        : activity.type.replace('_', ' ');
                      
                      // Translate activity status
                      const statusText = activity.status === 'posted' 
                        ? 'Posted' 
                        : activity.status === 'draft' 
                        ? 'Draft' 
                        : activity.status === 'paid'
                        ? 'Paid'
                        : activity.status === 'pending'
                        ? 'Pending'
                        : activity.status;
                      
                      // Process title - translate common patterns
                      let translatedTitle = activity.title?.replace(/\s*(PAYROLL-|payroll-)[a-z0-9]{15,}/gi, '').replace(/\s*-\s*[a-z0-9]{20,}\s*$/i, '').trim() || activity.title;
                      
                      // Translate description - extract and translate common patterns
                      let translatedDescription = activity.description || '';
                      
                      return (
                        <div key={activity.id} className="flex items-center gap-4 p-4 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-full ${
                              activity.type === 'journal_entry' ? 'bg-teal-100' : 'bg-blue-100'
                            }`}>
                              {activity.type === 'journal_entry' ? (
                                <Receipt className="w-4 h-4 text-teal-600" />
                              ) : (
                                <FileText className="w-4 h-4 text-blue-600" />
                              )}
                            </div>
                            <div className={`w-3 h-3 rounded-full ${
                              activity.status === 'posted' ? 'bg-green-500' : 
                              activity.status === 'draft' ? 'bg-yellow-500' : 
                              'bg-blue-500'
                            }`}></div>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {translatedTitle.toLowerCase().includes('payment to') ? (
                                <>
                                  <TranslatedText text="Payment to" /> {translatedTitle.replace(/payment to/gi, '').trim()}
                                </>
                              ) : translatedTitle.toLowerCase().includes('inventory delivered from po') ? (
                                <>
                                  <TranslatedText text="Inventory delivered from PO" /> {translatedTitle.replace(/inventory delivered from po/gi, '').trim()}
                                </>
                              ) : translatedTitle.toLowerCase().startsWith('invoice') ? (
                                <>
                                  <TranslatedText text="Invoice" /> {translatedTitle.replace(/invoice/gi, '').trim()}
                                </>
                              ) : (
                                translatedTitle
                              )}
                            </p>
                            <p className="text-xs text-gray-600">
                              {translatedDescription.toLowerCase().includes('payment to') ? (
                                <>
                                  <TranslatedText text="Payment to" /> {translatedDescription.replace(/payment to/gi, '').trim()}
                                </>
                              ) : translatedDescription.toLowerCase().includes('inventory delivered from po') ? (
                                <>
                                  <TranslatedText text="Inventory delivered from PO" /> {translatedDescription.replace(/inventory delivered from po/gi, '').trim()}
                                </>
                              ) : (
                                translatedDescription
                              )}
                            </p>
                            <p className="text-xs text-gray-500 capitalize">
                              <TranslatedText text={activityTypeText} /> • <TranslatedText text={statusText} />
                            </p>
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(activity.date).toLocaleDateString()}
                          </span>
                        </div>
                      );
                      })}
                    </>
                  ) : error && !(error.includes('No internet connection') || error.includes('network')) ? (
                    <div className="flex flex-col items-center justify-center py-8 text-red-600">
                      <AlertTriangle className="h-6 w-6 mr-2 mb-2" />
                      <span className="text-sm text-center">
                        <TranslatedText text="Error loading activity" />
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-8 text-gray-500">
                      <Activity className="h-6 w-6 mr-2" />
                      <span className="text-sm">
                        <TranslatedText text="No recent activity" />
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </PageLayout>
  )
}
